import type {
  CustomerAddressWriteable,
  CustomerGender,
} from '@packages/gql/generated/shopping/graphql';
import type { ChangeAddressFormData } from '../ChangeAddressForm/ChangeAddressForm.client';

/**
 * Converts form data to CustomerAddressWriteable with required countryCode.
 * This is a type-safe helper that makes the conversion explicit and leverages
 * TypeScript's structural typing to ensure compatibility at compile time.
 *
 * @param formData - The form data from ChangeAddressForm
 * @param countryCode - The country code (required field not in form)
 * @returns A properly typed CustomerAddressWriteable object
 */
export function toCustomerAddressWriteable(
  formData: ChangeAddressFormData,
  countryCode: string,
): CustomerAddressWriteable {
  // Explicitly map all required fields to ensure type safety
  return {
    firstName: formData.firstName ?? '',
    lastName: formData.lastName ?? '',
    street: formData.street ?? '',
    houseNumber: formData.houseNumber ?? '',
    zipCode: formData.zipCode ?? '',
    city: formData.city ?? '',
    countryCode,
    gender: (formData.gender as CustomerGender | undefined) ?? null,
    additional: formData.additional ?? null,
    honorific: null,
    primaryPhoneNumber: formData.primaryPhoneNumber ?? null,
    secondaryPhoneNumber: null,
    birthName: null,
    addressName: null,
  } satisfies CustomerAddressWriteable;
}

/**
 * Merges partial address data with a base address, maintaining type safety.
 * Useful when updating an existing address with partial form data.
 *
 * @param baseAddress - The base address to merge into
 * @param partialData - Partial address data to overlay
 * @returns A properly typed CustomerAddressWriteable object
 */
export function mergeToCustomerAddressWriteable(
  baseAddress: CustomerAddressWriteable | ChangeAddressFormData | Partial<CustomerAddressWriteable>,
  partialData:
    | Partial<ChangeAddressFormData>
    | Partial<CustomerAddressWriteable>
    | Record<string, unknown>,
): CustomerAddressWriteable {
  // Strip __typename to ensure the result is a valid mutation input
  const merged = Object.fromEntries(
    Object.entries({ ...baseAddress, ...partialData }).filter(([key]) => key !== '__typename'),
  ) as CustomerAddressWriteable;
  return merged;
}
